// src/auth/permissionsDrift.js
//
// "Permissions drift" = the server's answer contradicts the claims this browser
// is holding. It exists because those claims are LOGIN-BOUND: `tenants[]` and
// `customers[]` are minted into the JWT at POST /auth/login, and GET /auth/me
// rebuilds its response from a users row + those same token claims. So the 30s
// focus re-sync re-reads `is_superadmin` / `is_active` / profile from the DB
// (fresh) but replays `tenants` / `customers` back to us unchanged (stale).
//
// Two directions, one user-visible fact ("your permissions have changed — sign
// in again"), hence ONE shared string:
//   • revoked — the server refuses (403) something the local claims still allow;
//   • granted — a right was just handed out and the surface stays hidden here,
//     because nothing short of a new token can reveal it.
//
// ⛔ Never wire this to a "refresh permissions" control that only refetches
// /auth/me: it reads the same token and would report the stale state back as if
// it were fresh. The only honest remedy is a new token, and the backend mints one
// at POST /auth/login and nowhere else — so signing out and in IS the fix, which
// is what the copy says. AuthContext.reauthenticate() is the single swap point,
// should that ever change.

/** Direction of the drift. Kept for diagnostics only — the copy is shared. */
export const DRIFT_REVOKED = 'revoked';
export const DRIFT_GRANTED = 'granted';

let _drift = null;              // null | { direction, code }
const _subscribers = new Set();

function emit() {
  _subscribers.forEach((fn) => {
    try { fn(_drift); } catch { /* a bad subscriber must not break a fetch */ }
  });
}

/** Subscribe to drift changes. Returns an unsubscribe function. */
export function subscribePermissionsDrift(fn) {
  _subscribers.add(fn);
  return () => _subscribers.delete(fn);
}

/** Current drift, or null. */
export function getPermissionsDrift() {
  return _drift;
}

/**
 * Flag a drift. `direction` is DRIFT_REVOKED (a refusal contradicted the claims)
 * or DRIFT_GRANTED (a right was granted elsewhere and cannot appear until the
 * token is re-minted). Both render the same message.
 */
export function notePermissionsDrift(direction = DRIFT_REVOKED, code = null) {
  // First one wins: the remedy is identical, so re-flagging would only make the
  // banner flicker on every subsequent refusal.
  if (_drift) return _drift;
  _drift = { direction, code };
  emit();
  return _drift;
}

/** Clear the flag (dismiss, logout, successful re-authentication, tests). */
export function clearPermissionsDrift() {
  if (!_drift) return;
  _drift = null;
  emit();
}

function readClaims(key) {
  try {
    const raw = JSON.parse(localStorage.getItem(key) || 'null');
    return Array.isArray(raw) ? raw : [];
  } catch {
    return [];
  }
}

/**
 * Decide whether a refusal is drift, and flag it if so.
 *
 * Only refusals that CONTRADICT the local claims count. A 403 on a tenant the
 * FE already knows it lost is not drift — it is the two sides agreeing — and
 * telling that user to sign in again would be noise.
 *
 * @param {object}      refusal
 * @param {number}      refusal.status      HTTP status.
 * @param {string}      refusal.errorCode   `error_code` from the error envelope.
 * @param {object|null} [refusal.details]   `details` from the envelope (may carry customer_id).
 * @param {string|null} [refusal.tenantId]  Tenant the refused call was scoped to.
 * @returns {boolean} true when a drift was flagged.
 */
export function noteRefusalIfDrift({ status, errorCode, details, tenantId } = {}) {
  if (status !== 403) return false;

  if (errorCode === 'TENANT_ACCESS_DENIED') {
    // Drift only if our own `tenants[]` still lists the tenant the server just
    // refused. Without a tenant id we cannot tell the two cases apart, so we
    // stay quiet rather than accuse the session of being stale.
    if (!tenantId) return false;
    const member = readClaims('auth_tenants').some(t => t.tenant_id === tenantId);
    if (!member) return false;
    notePermissionsDrift(DRIFT_REVOKED, errorCode);
    return true;
  }

  if (errorCode === 'CUSTOMER_ADMIN_REQUIRED') {
    const customers = readClaims('auth_customers');
    const customerId = details?.customer_id || null;
    // With an id, mirror isCustomerAdminOf (id AND role). Without one, fall back
    // to the coarse pre-gate: admin of ANY customer while the server says no.
    const claimsAdmin = customerId
      ? customers.some(c => c.customer_id === customerId && c.role === 'admin')
      : customers.some(c => c.role === 'admin');
    if (!claimsAdmin) return false;
    notePermissionsDrift(DRIFT_REVOKED, errorCode);
    return true;
  }

  return false;
}
